import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { FlatTreeControl } from '@angular/cdk/tree';
import { Component, Input, OnInit } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ChartsService } from 'src/app/services/charts.service';
import { ClickStreamService } from 'src/app/services/click-stream.service';

export class Workspace {
  constructor(public name: string, public description: string, public guid: string) { }
}

interface WorkspaceNode {
  workspace: Workspace;
  children?: WorkspaceNode[];
}

interface FlatNode {
  expandable: boolean;
  name: string;
  level: number;
}

@Component({
  selector: 'app-admin-chart-picker',
  templateUrl: './admin-chart-picker.component.html',
  styleUrls: ['./admin-chart-picker.component.scss'],
})
export class AdminChartPickerComponent implements OnInit {

  @Input('selectedCharts') selectedCharts: any[];

  private _transformer = (node: WorkspaceNode, level: number) => {
    return {
      expandable: !!node.children && node.children.length > 0,
      name: node.workspace.name,
      guid: node.workspace.guid,
      description: node.workspace.description,
      level: level,
    };
  }

  treeControl = new FlatTreeControl<FlatNode>(
    node => node.level, node => node.expandable);

  treeFlattener = new MatTreeFlattener(
    this._transformer, node => node.level, node => node.expandable, node => node.children);

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  searchData: WorkspaceNode[] = []

  clickstreamPageTitle = 'Push Chart - Select Chart' // make sure to use input if we reuse component

  constructor(private chartsService: ChartsService, private clickstream: ClickStreamService) { }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  ngOnInit(): void {
    this.dataSource.data = this.searchData;
    this.search();
    this.searchFilter.pipe(debounceTime(500), distinctUntilChanged())
      .subscribe(value => {
        if (value && value.length >= 2) {
          this.search();
        }
      });
  }

  searchError = false;
  searchValue: string = '';
  searchFilter: Subject<string> = new Subject<string>();
  isLoading = true;
  viewTags = false;
  selectedTags: string[] = [];
  tagSearchResults: string[] = [];
  readonly separatorKeysCodes: number[] = [ENTER, COMMA];

  searchChanged() {
    this.searchFilter.next();
  }

  search() {
    this.isLoading = true;
    this.chartsService.searchCharts(this.searchValue, this.selectedTags).subscribe(
      (data: any) => {
        if (data.results && data.results.length > 0) {
          var workspaces: WorkspaceNode[] = [];
          for (let ws of data.results) {
            var newWs = new Workspace(ws.name, ws.description, ws.guid);
            var chartsInWs: WorkspaceNode[] = [];
            for (let c of ws.charts) {
              var newChart = new Workspace(c.name, c.description, c.guid);
              var newChildNode: WorkspaceNode = {
                workspace: newChart
              };
              chartsInWs.push(newChildNode);
            }
            let newWsNode: WorkspaceNode = {
              workspace: newWs,
              children: chartsInWs
            }
            workspaces.push(newWsNode);
          }
          this.dataSource.data = workspaces;
        } else {
          this.searchError = true;
        }
        this.isLoading = false;
      }, err => {
        this.searchError = true;
        this.isLoading = false;
      })
    this.clickstream.send('search-charts', 'search', this.clickstreamPageTitle, 'Search Text Input', '', true);
  }

  selectChart(chart: Workspace) {
    if (this.isSelected(chart)) {
      this.selectedCharts.splice(this.selectedCharts.findIndex(c => c.guid === chart.guid), 1);
    } else {
      this.selectedCharts.push(chart);
    }
    this.clickstream.send('select-chart', 'click', this.clickstreamPageTitle, 'Select Chart', '', true);
  }

  isSelected(node: Workspace): boolean {
    return this.selectedCharts.some(c => c.guid == node.guid || c.name == node.name);
  }

  goToTags() {
    this.viewTags = true;
    if (this.tagSearchResults.length == 0) {
      this.chartsService.getTags().subscribe((tags: any[]) => {
        this.tagSearchResults = tags.map(t => t.tag);
      })
    }
    this.clickstream.send('filter-by-tag-button-click', 'click', this.clickstreamPageTitle, 'Filter By Super Tag Button', '', true);
  }

  goToSearch() {
    this.viewTags = false;
  }

  removeTag(tag: string) {
    const index = this.selectedTags.indexOf(tag);

    if (index >= 0) {
      this.selectedTags.splice(index, 1);
      this.search();
    }
    this.clickstream.send('remove-tag', 'click', this.clickstreamPageTitle, 'Remove Super Tag', '', true);
  }

  selectTag(tag: string) {
    if (this.selectedTags.indexOf(tag) == -1) {
      this.selectedTags.push(tag);
      this.viewTags = false;
      this.search();
    }
    this.clickstream.send('filter-by-tag', 'click', this.clickstreamPageTitle, 'Super Tag', '', true);
  }

}
